import { ITeam } from 'interfaces/team/ITeam';
import lunr from 'lunr';
import { action, makeObservable, observable } from 'mobx';
import { OfflineError } from 'types/OfflineError';
import { ContextualService } from '../services/data-services/ContextualService';
import { LogService } from '../services/data-services/LogService';
import { BaseStore } from './BaseStore';
import { NotificationStore } from './NotificationStore';

export class TeamStore extends BaseStore {
  constructor(contextualService: ContextualService, logService: LogService, notificationStore: NotificationStore) {
    super(logService);
    this.contextualService = contextualService;
    this.notificationStore = notificationStore;
    makeObservable(this);
  }

  private contextualService: ContextualService;

  private notificationStore: NotificationStore;

  private teamIndex: lunr.Index = lunr(() => {});

  @observable teams = [] as ITeam[];

  searchTeamsByName = (query: string, filterId?: string): ITeam[] => {
    query = `+${query.trim().split(' ').join('* +')}*`;
    const res = this.teamIndex.search(query);
    if (filterId) {
      return res
        .map((v: lunr.Index.Result) => this.teams.find((t) => t.id === v.ref) as ITeam)
        .filter((item) => {
          return item.id !== filterId;
        });
    }
    return res.map((v: lunr.Index.Result) => this.teams.find((t) => t.id === v.ref) as ITeam);
  };

  @action setTeams = (data: ITeam[]) => {
    this.teams = data;
    this.initializeIndex();
  };

  @action fetchTeams = async () => {
    try {
      await this.fetchData(this.contextualService.GetAllTeams, this.setTeams);
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-fetching-teams',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-fetching-teams');
          },
        });
      }
    }
  };

  @action fetchPlayers = async (teamId: string) => {
    try {
      return await this.contextualService.GetTeamRoster(teamId);
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-fetching-players',
          message: err.message,
          variant: err instanceof OfflineError ? 'warning' : 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-fetching-players');
          },
        });
      }
    }
  };

  getTeamById = (teamId: string) => this.teams.find((t) => t.id === teamId);

  initializeIndex = () => {
    const { teams } = this;
    this.teamIndex = lunr(function () {
      this.pipeline.remove(lunr.stemmer);
      this.pipeline.remove(lunr.stopWordFilter);
      this.field('name');
      this.ref('id');

      teams.forEach((element) => {
        this.add(element);
      });
    });
  };
}
